import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import ReactSwitch from 'react-switch';
import ReactTooltip from 'react-tooltip';
import cn from 'classnames';
import { useAuthUser } from 'react-auth-kit';
import { useAtomValue } from 'jotai/utils';
import { CodeSimple, Copy, DownloadSimple, Eye, Pencil } from 'src/common/icons';
import { getAssetMediaUrl, processAssetConversions } from 'src/api/functions/assetsApi';
import { isErrorResponseResult } from 'src/api/utils/errorHandlers';
import { useConversionPublicAccessMutation } from 'src/api/mutations/assetContentAccessMutations';
import { useAccessToken } from 'src/hooks/useAccessToken';
import useRoleManager from 'src/hooks/useRoleManager';
import useModal from 'src/hooks/useModal';
import { useAlerts } from 'src/common/AlertManager';
import { Asset } from 'src/types/TAssets';
import {
  conversionExtension,
  humanizeConversionName,
  isPreviewableConversion,
  isStreamingOnly,
  isVideoConversion,
} from 'src/utils/conversionLabels';
import { buildIframeEmbedSnippet } from 'src/utils/previews';
import ConfirmDialog from 'src/components/ConfirmDialog';
import BaseModal from 'src/components/BaseModal/BaseModal';
import AssetLinkDialog from 'src/components/AssetLinkDialog/AssetLinkDialog';
import Button from 'src/components/Button';
import VideoJsPlayer from 'src/components/VideoJsPlayer/VideoJsPlayer';
import { formatSeconds } from 'src/utils/transform';
import { settingsAtom } from 'src/shared/settings/index';
import { StoryboardSource, StoryboardStrip, StoryboardViewerModal, storyboardSourceFor } from 'src/components/Storyboard';
import { ConversionData, EDITION_MODE, SubtitleData } from '../ModalEditor';
import messages from './messages';
import styles from './ConversionsTab.module.scss';

// `error_log` the transcode task writes on a format it refused for lack of
// credits (filespin/core/credits/video.py INSUFFICIENT_CREDITS_MESSAGE).
const INSUFFICIENT_CREDITS_ERROR = 'Not enough credits';
const ADD_CREDITS_ROUTE = '/admin/subscription?purchase=credits';
// The one key POST /assets/{id}/process will not take with any other.
const CLIP_PREVIEW_KEY = 'clip_preview';

type ConversionsTabProps = {
  asset: Asset;
  onEditConversion: (type: EDITION_MODE, data: ConversionData | SubtitleData) => void;
};

const EmbedSnippetDialog = ({ assetId, conversionKey }: { assetId: string; conversionKey: string }) => {
  const { formatMessage } = useIntl();
  const { showInfo } = useAlerts();
  const snippet = buildIframeEmbedSnippet(assetId, conversionKey);

  const copySnippet = async () => {
    await navigator.clipboard.writeText(snippet);
    showInfo(formatMessage(messages.embedCopied));
  };

  return (
    <BaseModal containerStyle={{ width: 'min(560px, 92vw)' }}>
      <div className={styles.embedDialog}>
        <h3>{formatMessage(messages.embedTitle)}</h3>
        <p>{formatMessage(messages.embedHelp)}</p>
        <textarea readOnly value={snippet} onFocus={(e) => e.target.select()} />
        <div className={styles.embedActions}>
          <Button mode="primary" size="small" icon={<Copy />} onClick={copySnippet}>
            {formatMessage(messages.embedCopy)}
          </Button>
        </div>
      </div>
    </BaseModal>
  );
};

export const ConversionsTab = ({ asset, onEditConversion }: ConversionsTabProps) => {
  const { formatMessage } = useIntl();
  const auth = useAuthUser();
  const accessToken = useAccessToken();
  const { showAlert, showInfo } = useAlerts();
  const { isUser, isAdmin, isManager } = useRoleManager();
  const { showModal } = useModal();

  const [busyLinkKeys, setBusyLinkKeys] = useState<Record<string, boolean>>({});
  const [pendingPublicRequest, setPendingPublicRequest] = useState<{
    conversionKey: string;
    isPublic: boolean;
  } | null>(null);
  const [togglingKey, setTogglingKey] = useState<string | null>(null);
  // inline preview reveal below the clicked row (#932 feedback: never hijack
  // the main preview panel for conversion previews)
  const [previewKey, setPreviewKey] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [previewLoading, setPreviewLoading] = useState(false);

  const publicAccessMutation = useConversionPublicAccessMutation(asset.id, {
    onSuccess: (_data, variables) => {
      showInfo(formatMessage(variables.isPublic ? messages.nowPublic : messages.nowPrivate));
    },
    onError: () => {
      showAlert(formatMessage(messages.toggleFailed));
    },
    onSettled: () => {
      setTogglingKey(null);
    },
  });

  // Only for the "keyframes" check (frame times can't be told then). Read
  // from the atom the asset page already filled; no fetch of its own.
  const storyboardMode: string | undefined = useAtomValue(settingsAtom)?.video?.thumbnails?.storyboard?.mode;

  const contentType = asset?.content_type?.split('/')[0];
  const conversionEntries = Object.entries(asset.conversions || {});
  // Formats refused for lack of credits (conf/22): they have no conversion
  // row, only an entry in `errors` — so without this they would simply be
  // missing, with nothing to say why.
  const creditSkippedKeys = Object.entries(asset.errors || {})
    .filter(([key, message]) => message === INSUFFICIENT_CREDITS_ERROR
      && !(asset.conversions || {})[key])
    .map(([key]) => key);
  // Per-format state of a re-run started from the notice: 'busy' while its
  // request is in flight, 'queued' once the server accepted it (the format
  // then appears in the table when the transcode finishes).
  const [processState, setProcessState] = useState<Record<string, 'busy' | 'queued'>>({});
  const anyBusy = Object.values(processState).includes('busy');
  const pendingKeys = creditSkippedKeys.filter((key) => processState[key] !== 'queued');

  const setKeysState = (keys: string[], state?: 'busy' | 'queued') =>
    setProcessState((prev) => {
      const next = { ...prev };
      keys.forEach((key) => {
        if (state) next[key] = state;
        else delete next[key];
      });
      return next;
    });

  const processSkipped = async (keys: string[]) => {
    // POST /process refuses `clip_preview` alongside any other key (400), so
    // it always goes in a request of its own.
    const batches = [
      keys.filter((key) => key !== CLIP_PREVIEW_KEY),
      keys.filter((key) => key === CLIP_PREVIEW_KEY),
    ].filter((batch) => batch.length > 0);

    let queuedAny = false;
    for (const batch of batches) {
      setKeysState(batch, 'busy');
      const res = await processAssetConversions(asset.id, batch, accessToken);
      if (isErrorResponseResult(res)) {
        setKeysState(batch);
        const body = (res.error || {}) as { error?: string; required?: number; balance?: number };
        showAlert(
          res.status === 402 && body.error === 'insufficient_credits'
            ? formatMessage(messages.processStillShort, {
                required: body.required ?? '?',
                balance: body.balance ?? '?',
              })
            : formatMessage(messages.processFailed),
        );
        // Short of credits for one batch means short for the next as well.
        break;
      }
      setKeysState(batch, 'queued');
      queuedAny = true;
    }
    if (queuedAny) showInfo(formatMessage(messages.processQueued));
  };

  const creditsSkippedNotice = creditSkippedKeys.length > 0 && (
    <div className={styles.creditsSkipped} role="status" data-test="credits-skipped">
      <div className={styles.creditsSkippedHead}>
        <div>
          <p className={styles.creditsSkippedTitle}>
            {formatMessage(messages.creditsSkippedTitle, { count: creditSkippedKeys.length })}
          </p>
          <p>{formatMessage(messages.creditsSkippedBody)}</p>
        </div>
        {isAdmin && (
          <Link className={styles.creditsSkippedLink} to={ADD_CREDITS_ROUTE}>
            {formatMessage(messages.addCredits)}
          </Link>
        )}
      </div>
      <ul className={styles.creditsSkippedList}>
        {creditSkippedKeys.map((key) => {
          const state = processState[key];
          return (
            <li key={key} data-test={`credits-skipped-${key}`}>
              <span title={key}>{humanizeConversionName(key)}</span>
              {state === 'queued' ? (
                <span className={styles.creditsSkippedQueued}>{formatMessage(messages.processQueuedShort)}</span>
              ) : (
                !isUser && (
                  <Button mode="tertiary" size="small" onClick={() => processSkipped([key])} disabled={anyBusy}>
                    {formatMessage(state === 'busy' ? messages.processBusy : messages.processOne)}
                  </Button>
                )
              )}
            </li>
          );
        })}
      </ul>
      {!isUser && pendingKeys.length > 1 && (
        <div className={styles.creditsSkippedActions}>
          <Button mode="primary" size="small" onClick={() => processSkipped(pendingKeys)} disabled={anyBusy}>
            {formatMessage(messages.processAll)}
          </Button>
        </div>
      )}
    </div>
  );
  const canToggle = isAdmin || isManager;

  // data-tip contents change with state (previewing, public/private);
  // a single ReactTooltip instance needs a rebuild to pick them up
  useEffect(() => {
    ReactTooltip.rebuild();
  });

  // close the reveal when navigating to another asset
  useEffect(() => {
    setPreviewKey(null);
    setPreviewUrl('');
  }, [asset.id]);

  const requestPublicToggle = (conversionKey: string, isPublic: boolean) => {
    if (isPublic) {
      // going public deserves a stop-and-think: the link becomes open + CDN-cached
      setPendingPublicRequest({ conversionKey, isPublic });
    } else {
      runPublicToggle(conversionKey, isPublic);
    }
  };

  const runPublicToggle = (conversionKey: string, isPublic: boolean) => {
    setTogglingKey(conversionKey);
    publicAccessMutation.mutate({ conversionKey, isPublic });
  };

  const isStoryboardRow = (conversionKey: string) => conversionKey.toLowerCase() === 'storyboard';

  const isEditableRow = (conversionKey: string): boolean => {
    if (isUser) return false;
    const isSubtitle = /^subtitles_\w+/i.test(conversionKey);
    if (isSubtitle) return true;
    return contentType === 'image' && !isStreamingOnly(conversionKey);
  };

  const handleEditConversion = async (conversionKey: string) => {
    const url = await getAssetMediaUrl(asset.id, conversionKey, auth()!.accessId, 'download', accessToken);
    const isSubtitle = /^subtitles_\w+/i.test(conversionKey);

    if (isSubtitle) {
      onEditConversion(EDITION_MODE.SUBTITLE, { key: conversionKey, url });
    } else {
      onEditConversion(EDITION_MODE.CONVERSION, { key: conversionKey, name: asset.name, url });
    }
  };

  const storyboard = storyboardSourceFor(asset, { mode: storyboardMode, jwt: accessToken });

  const openStoryboardViewer = (source: StoryboardSource, frame?: number) => {
    showModal(<StoryboardViewerModal {...source} initialFrame={frame} canSetThumbnail={!isUser} />);
  };

  const handlePreviewToggle = async (conversionKey: string) => {
    if (previewKey === conversionKey) {
      setPreviewKey(null);
      setPreviewUrl('');
      return;
    }
    // Frames come straight from the imaging route; there is no single file
    // to get a link for.
    if (isStoryboardRow(conversionKey)) {
      setPreviewKey(conversionKey);
      setPreviewUrl('');
      return;
    }
    setPreviewKey(conversionKey);
    setPreviewUrl('');
    setPreviewLoading(true);
    try {
      const url = await getAssetMediaUrl(asset.id, conversionKey, auth()!.accessId, 'display', accessToken);
      if ((url as any)?.status) {
        showAlert(formatMessage(messages.previewFailed));
        setPreviewKey(null);
        return;
      }
      setPreviewUrl(url as string);
    } catch (error) {
      showAlert(formatMessage(messages.previewFailed));
      setPreviewKey(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDownload = async (conversionKey: string) => {
    setBusyLinkKeys((prev) => ({ ...prev, [`dl:${conversionKey}`]: true }));
    try {
      const url = await getAssetMediaUrl(asset.id, conversionKey, auth()!.accessId, 'download', accessToken);
      if ((url as any).status === 404) {
        showAlert(formatMessage(messages.downloadFailed));
        return;
      }
      window.open(url);
    } finally {
      setBusyLinkKeys((prev) => ({ ...prev, [`dl:${conversionKey}`]: false }));
    }
  };

  const handleLinks = (conversionKey: string) => {
    showModal(
      <AssetLinkDialog assetId={asset.id} assetName={asset.name} conversionKey={conversionKey} />,
    );
  };

  const handleEmbed = (conversionKey: string) => {
    showModal(<EmbedSnippetDialog assetId={asset.id} conversionKey={conversionKey} />);
  };

  if (conversionEntries.length === 0) {
    return (
      <>
      {creditsSkippedNotice}
      <div className={styles.emptyState}>
        <h4>{formatMessage(messages.emptyTitle)}</h4>
        <p>{formatMessage(messages.emptyExplainer)}</p>
        {(isAdmin || isManager) && (
          <Link to={contentType === 'video' ? '/admin/settings/video' : '/admin/settings/image'}>
            {formatMessage(messages.emptyConfigureLink)}
          </Link>
        )}
      </div>
      </>
    );
  }

  return (
    <div className={styles.container}>
      {creditsSkippedNotice}
      {/* single tooltip instance for the whole tab — one per data-tip element
          renders a duplicate "shadow" tooltip box behind the visible one */}
      <ReactTooltip effect="solid" />
      <table className={styles.table}>
        <thead>
          <tr>
            <th>{formatMessage(messages.colName)}</th>
            <th>{formatMessage(messages.colType)}</th>
            <th>
              <span className={styles.publicHeader} data-tip={formatMessage(messages.publicColumnHint)}>
                {formatMessage(messages.colPublic)}
              </span>
            </th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {conversionEntries.map(([conversionKey, conversion]) => {
            const extension = conversionExtension(conversion);
            const hasError = !!asset?.errors?.[conversionKey];
            const isVideoRow = isVideoConversion(conversionKey, conversion);
            const isStoryboard = isStoryboardRow(conversionKey) && !!storyboard;
            const previewable = isStoryboard || (!hasError && isPreviewableConversion(conversionKey, conversion));
            const isPreviewing = previewKey === conversionKey;
            const isToggling = togglingKey === conversionKey;
            const embeddable = isVideoRow && !hasError;

            return (
              <React.Fragment key={conversionKey}>
                <tr className={cn({ [styles.previewingRow]: isPreviewing })}>
                  <td className={styles.nameCell}>
                    <span className={styles.nameLabel} data-tip={conversionKey} title={conversionKey}>
                      {humanizeConversionName(conversionKey)}
                    </span>
                    <span className={styles.nameMeta}>
                      {[
                        typeof conversion.width === 'number' &&
                        conversion.width > 0 &&
                        typeof conversion.height === 'number' &&
                        conversion.height > 0
                          ? `${conversion.width}×${conversion.height}`
                          : null,
                        (contentType === 'video' || contentType === 'audio') && conversion.duration
                          ? formatSeconds(conversion.duration)
                          : null,
                        contentType === 'image' && conversion.dpi ? `${conversion.dpi} DPI` : null,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </td>
                  <td>{extension && <span className={styles.typeChip}>{extension}</span>}</td>
                  <td className={styles.publicCell}>
                    {canToggle ? (
                      <>
                        <ReactSwitch
                          checked={!!conversion.public}
                          onChange={(checked: boolean) => requestPublicToggle(conversionKey, checked)}
                          disabled={publicAccessMutation.isLoading}
                          checkedIcon={false}
                          uncheckedIcon={false}
                          onColor="#2e53e3"
                          width={40}
                          height={20}
                          handleDiameter={16}
                        />
                        {isToggling && <span className={styles.toggleSpinner} />}
                      </>
                    ) : (
                      <span className={cn(styles.badge, conversion.public ? styles.public : styles.private)}>
                        {formatMessage(conversion.public ? messages.publicBadge : messages.privateBadge)}
                      </span>
                    )}
                  </td>
                  <td className={styles.actionsCell}>
                    {hasError ? (
                      <button
                        type="button"
                        data-tip={asset.errors[conversionKey]}
                        className={styles.errorBadge}
                      >
                        {formatMessage(messages.errorBadge)}
                      </button>
                    ) : (
                      <>
                        {previewable && (
                          <button
                            type="button"
                            className={cn(styles.iconButton, { [styles.active]: isPreviewing })}
                            onClick={() => handlePreviewToggle(conversionKey)}
                            data-tip={formatMessage(isPreviewing ? messages.closePreviewTip : messages.previewTip)}
                          >
                            <Eye />
                          </button>
                        )}
                        {isEditableRow(conversionKey) && (
                          <button
                            type="button"
                            className={styles.iconButton}
                            onClick={() => handleEditConversion(conversionKey)}
                            data-tip={formatMessage(messages.editTip)}
                          >
                            <Pencil />
                          </button>
                        )}
                        {!isStreamingOnly(conversionKey) && (
                          <button
                            type="button"
                            className={styles.iconButton}
                            disabled={!!busyLinkKeys[`dl:${conversionKey}`]}
                            onClick={() => handleDownload(conversionKey)}
                            data-tip={formatMessage(messages.downloadTip)}
                          >
                            <DownloadSimple />
                          </button>
                        )}
                        {(!isStreamingOnly(conversionKey) || (conversionKey === 'hls-video' && conversion.public)) && (
                          <button
                            type="button"
                            className={styles.iconButton}
                            onClick={() => handleLinks(conversionKey)}
                            data-tip={formatMessage(messages.linksTip)}
                          >
                            <Copy />
                          </button>
                        )}
                        {embeddable && (
                          <button
                            type="button"
                            className={styles.iconButton}
                            disabled={!conversion.public}
                            onClick={() => conversion.public && handleEmbed(conversionKey)}
                            data-tip={formatMessage(
                              conversion.public ? messages.embedTip : messages.embedNeedsPublicTip,
                            )}
                          >
                            <CodeSimple />
                          </button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
                {isPreviewing && (
                  <tr className={styles.previewRevealRow}>
                    <td colSpan={4}>
                      <div className={styles.previewReveal}>
                        {isStoryboard && storyboard ? (
                          <StoryboardStrip
                            {...storyboard}
                            onOpenViewer={(frame) => openStoryboardViewer(storyboard, frame)}
                          />
                        ) : previewLoading || !previewUrl ? (
                          <div className={styles.previewLoading}>
                            <span className={styles.toggleSpinner} />
                            {formatMessage(messages.previewLoading)}
                          </div>
                        ) : isVideoRow ? (
                          <VideoJsPlayer src={previewUrl} fluid />
                        ) : (
                          <img
                            className={styles.previewImage}
                            src={previewUrl}
                            alt={humanizeConversionName(conversionKey)}
                            data-test="conversion-preview-image"
                          />
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
      {pendingPublicRequest && (
        <ConfirmDialog
          title={formatMessage(messages.makePublicTitle)}
          message={formatMessage(messages.makePublicMessage)}
          onConfirm={() => {
            runPublicToggle(pendingPublicRequest.conversionKey, pendingPublicRequest.isPublic);
            setPendingPublicRequest(null);
          }}
          onCancel={() => setPendingPublicRequest(null)}
        />
      )}
    </div>
  );
};

export default ConversionsTab;
